/**
 * Shared customer journey state (pure, unit-tested): the vehicle selected on
 * `/` or `/vehicles`, the product families the customer EXPLICITLY selected
 * (never a page view, scroll or hover — see docs/visual-experience-spec.md
 * §M.3 / decision F2), and the service chosen before booking. Carries across
 * `/`, `/vehicles`, `/products`, `/products/:code` and `/book`, including the
 * sign-in detour (the provider persists it to sessionStorage; see
 * JourneyProvider). Only non-sensitive vehicle-master ids and catalogue
 * codes are ever stored — no personal data.
 */
import type { VehicleSelection } from '@/types/domain';

export const EMPTY_VEHICLE: VehicleSelection = { makeId: null, modelId: null, yearId: null, trimId: null };

/** Matches booking_product_interests.source (private/database/…/2000_media_sources_and_interests.sql). */
export type InterestSource = 'landing_selection' | 'product_page' | 'booking_form';
const INTEREST_SOURCES: readonly InterestSource[] = ['landing_selection', 'product_page', 'booking_form'];

export interface JourneyInterest {
  /** product_families.code — never a database id, so it stays meaningful even before the family is looked up. */
  code: string;
  source: InterestSource;
}

export interface JourneyState {
  vehicle: VehicleSelection;
  /** Explicitly selected product families, in selection order. No numerical limit (decision F2). */
  interests: JourneyInterest[];
  serviceId: string | null;
}

export const EMPTY_JOURNEY: JourneyState = { vehicle: EMPTY_VEHICLE, interests: [], serviceId: null };

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Matches the `code` check constraints on product_families / services (`^[a-z0-9\-]+$`). */
const CODE_RE = /^[a-z0-9-]{1,60}$/;

function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value);
}

function isCode(value: unknown): value is string {
  return typeof value === 'string' && CODE_RE.test(value);
}

function readUuid(value: unknown): string | null {
  return isUuid(value) ? value : null;
}

function parseVehicle(value: unknown): VehicleSelection {
  const v = (value ?? {}) as Record<string, unknown>;
  return {
    makeId: readUuid(v.makeId),
    modelId: readUuid(v.modelId),
    yearId: readUuid(v.yearId),
    trimId: readUuid(v.trimId),
  };
}

function parseInterests(value: unknown): JourneyInterest[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const out: JourneyInterest[] = [];
  for (const row of value) {
    const r = (row ?? {}) as Record<string, unknown>;
    if (!isCode(r.code) || seen.has(r.code)) continue;
    const source = INTEREST_SOURCES.includes(r.source as InterestSource) ? (r.source as InterestSource) : null;
    if (!source) continue;
    seen.add(r.code);
    out.push({ code: r.code, source });
  }
  return out;
}

/** Defensive parse: sessionStorage content (or a deep-link) is untrusted input, never assumed well-formed. */
export function parseJourneyState(raw: unknown): JourneyState {
  const v = (raw ?? {}) as Record<string, unknown>;
  return {
    vehicle: parseVehicle(v.vehicle),
    interests: parseInterests(v.interests),
    serviceId: readUuid(v.serviceId),
  };
}

export function parseJourneyJson(json: string | null): JourneyState {
  if (!json) return EMPTY_JOURNEY;
  try {
    return parseJourneyState(JSON.parse(json));
  } catch {
    return EMPTY_JOURNEY;
  }
}

export function serializeJourneyState(state: JourneyState): string {
  return JSON.stringify(state);
}

export function isVehicleComplete(vehicle: VehicleSelection): boolean {
  return !!(vehicle.makeId && vehicle.modelId && vehicle.yearId);
}

export function withVehicle(state: JourneyState, vehicle: VehicleSelection): JourneyState {
  return { ...state, vehicle };
}

export function withService(state: JourneyState, serviceId: string | null): JourneyState {
  return { ...state, serviceId };
}

/** Explicit selection only — idempotent (selecting the same family again is a no-op, not a duplicate). */
export function addInterest(state: JourneyState, code: string, source: InterestSource): JourneyState {
  if (!isCode(code) || state.interests.some((i) => i.code === code)) return state;
  return { ...state, interests: [...state.interests, { code, source }] };
}

export function removeInterest(state: JourneyState, code: string): JourneyState {
  if (!state.interests.some((i) => i.code === code)) return state;
  return { ...state, interests: state.interests.filter((i) => i.code !== code) };
}

export function toggleInterest(state: JourneyState, code: string, source: InterestSource): JourneyState {
  return state.interests.some((i) => i.code === code) ? removeInterest(state, code) : addInterest(state, code, source);
}

export function hasInterest(state: JourneyState, code: string): boolean {
  return state.interests.some((i) => i.code === code);
}
